import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import UnarchiveOutlinedIcon from "@mui/icons-material/UnarchiveOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { loadConnectAndInitialize } from "@stripe/connect-js";
import TeacherNavbar from "../components/TeacherNavbar";
import { db } from "../firebaseConfig";
import { getAuth } from "firebase/auth";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { useTranslation } from "react-i18next";
import {
  getMissingStripeTeacherFields,
  STRIPE_CONNECT_DOC_ID,
  STRIPE_CONNECT_SUBCOLLECTION,
  STRIPE_TEACHER_DEFAULTS,
} from "../utils/stripeTeacherDefaults";

const REGION_OPTIONS = [
  { key: "algeria", label: "Algeria" },
  { key: "north_africa", label: "North Africa" },
  { key: "turkey", label: "Turkey" },
  { key: "europe", label: "Europe" },
  { key: "rest_world", label: "Rest of the world" },
];

const CURRENCY_OPTIONS = ["DA", "€", "₺", "$"];
const ONBOARDING_ALLOWED_COUNTRIES = ["GB", "FR", "BE"];
const STRIPE_CONNECT_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "";

const BILLING_OPTIONS = [
  { key: "one_time", label: "One time" },
  { key: "recurring", label: "Recurring" },
];

const STATUS_FILTERS = ["all", "published", "archived"];

const EMPTY_SERVICE_FORM = {
  name: "",
  description: "",
};

const EMPTY_PRICE_FORM = {
  regionKey: "algeria",
  currency: "DA",
  amount: "",
  billingType: "one_time",
  active: true,
};

function statusChipSx(status) {
  if (status === "published") {
    return {
      bgcolor: "rgba(34,197,94,0.12)",
      color: "rgba(22,163,74,0.95)",
      borderRadius: "10px",
      fontWeight: 700,
      height: 28,
    };
  }
  if (status === "archived") {
    return {
      bgcolor: "rgba(148,163,184,0.18)",
      color: "rgba(71,85,105,0.95)",
      borderRadius: "10px",
      fontWeight: 700,
      height: 28,
    };
  }
  return {
    bgcolor: "rgba(251,191,36,0.18)",
    color: "#b45309",
    borderRadius: "999px",
    fontWeight: 700,
    height: 28,
  };
}

function TeacherServicesPage() {
  const { t } = useTranslation();

  const [teacherDocId, setTeacherDocId] = useState("");
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  const [statusFilter, setStatusFilter] = useState("all");

  const [serviceModalOpen, setServiceModalOpen] = useState(false);
  const [editingServiceId, setEditingServiceId] = useState("");
  const [serviceForm, setServiceForm] = useState(EMPTY_SERVICE_FORM);
  const [modalPrices, setModalPrices] = useState([]);
  const [priceForm, setPriceForm] = useState(EMPTY_PRICE_FORM);
  const [editingPriceRegion, setEditingPriceRegion] = useState("");

  const [savingService, setSavingService] = useState(false);
  const [stripeConnectData, setStripeConnectData] = useState(null);
  const [supportedCountries, setSupportedCountries] = useState([]);
  const [selectedOnboardingCountry, setSelectedOnboardingCountry] = useState("");
  const [loadingSupportedCountries, setLoadingSupportedCountries] = useState(false);
  const [startingOnboarding, setStartingOnboarding] = useState(false);
  const onboardingRedirectingRef = useRef(false);
  const pageAliveRef = useRef(true);
  const notificationBannerContainerRef = useRef(null);
  const notificationBannerElementRef = useRef(null);
  const [stripeRefreshInProgress, setStripeRefreshInProgress] = useState(false);
  const [notificationBannerError, setNotificationBannerError] = useState("");

  const filteredOnboardingCountries = useMemo(() => {
    return supportedCountries.filter((countryCode) =>
      ONBOARDING_ALLOWED_COUNTRIES.includes(String(countryCode || "").toUpperCase())
    );
  }, [supportedCountries]);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [formError, setFormError] = useState("");

  const notify = useCallback((message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  }, []);

  const filteredServices = useMemo(() => {
    return services
      .filter((service) => statusFilter === "all" || service.status === statusFilter)
      .sort((a, b) => (b.updatedAtMs || 0) - (a.updatedAtMs || 0));
  }, [services, statusFilter]);

  const mapRegionLabel = useCallback(
    (regionKey) => REGION_OPTIONS.find((r) => r.key === regionKey)?.label || regionKey,
    []
  );

  const resolveTeacherDocId = useCallback(async () => {
    const uid = getAuth().currentUser?.uid;
    if (!uid) throw new Error(t("Unable to identify the current teacher. Please log in again."));

    const snap = await getDocs(query(collection(db, "teachers"), where("uid", "==", uid)));
    if (snap.empty) throw new Error(t("Teacher not found in Firestore."));

    return snap.docs[0].id;
  }, [t]);

  const ensureStripeConnectSchema = useCallback(async (resolvedTeacherDocId) => {
    const stripeDocRef = doc(
      db,
      "teachers",
      resolvedTeacherDocId,
      STRIPE_CONNECT_SUBCOLLECTION,
      STRIPE_CONNECT_DOC_ID
    );
    const stripeSnap = await getDoc(stripeDocRef);
    const stripeData = stripeSnap.exists() ? stripeSnap.data() || {} : {};
    const missingFields = getMissingStripeTeacherFields(stripeData);

    if (!stripeSnap.exists()) {
      await setDoc(stripeDocRef, STRIPE_TEACHER_DEFAULTS, { merge: true });
      return STRIPE_TEACHER_DEFAULTS;
    }

    if (Object.keys(missingFields).length > 0) {
      await setDoc(stripeDocRef, missingFields, { merge: true });
      return { ...stripeData, ...missingFields };
    }

    return stripeData;
  }, []);

  const fetchServices = useCallback(async (resolvedTeacherDocId) => {
    const servicesSnap = await getDocs(collection(db, "teachers", resolvedTeacherDocId, "services"));

    const list = await Promise.all(
      servicesSnap.docs.map(async (serviceDoc) => {
        const data = serviceDoc.data() || {};
        const pricesSnap = await getDocs(
          collection(db, "teachers", resolvedTeacherDocId, "services", serviceDoc.id, "prices")
        );
        const prices = pricesSnap.docs.map((priceDoc) => ({ id: priceDoc.id, ...priceDoc.data() }));

        const normalizedStatus = data.status === "archived" ? "archived" : "published";

        return {
          id: serviceDoc.id,
          name: data.name || "",
          description: data.description || "",
          status: normalizedStatus,
          updatedAtMs: data.updatedAt?.toMillis ? data.updatedAt.toMillis() : 0,
          prices,
        };
      })
    );

    list.sort((a, b) => b.updatedAtMs - a.updatedAtMs);
    setServices(list);
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const resolvedTeacherDocId = await resolveTeacherDocId();
        setTeacherDocId(resolvedTeacherDocId);
        const stripeState = await ensureStripeConnectSchema(resolvedTeacherDocId);
        setStripeConnectData(stripeState);
        await fetchServices(resolvedTeacherDocId);
      } catch (error) {
        console.error(error);
        notify(error.message || t("An error occurred while loading data"), "error");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [ensureStripeConnectSchema, fetchServices, notify, resolveTeacherDocId, t]);

  const stripeOnboardingComplete = useMemo(() => {
    if (!stripeConnectData) return false;
    return Boolean(
      stripeConnectData.detailsSubmitted &&
        stripeConnectData.chargesEnabled &&
        stripeConnectData.payoutsEnabled
    );
  }, [stripeConnectData]);

  const stripeOnboardingStatus = useMemo(
    () => String(stripeConnectData?.stripeOnboardingStatus || "").toLowerCase(),
    [stripeConnectData]
  );

  const hasLinkedBankAccount = useMemo(
    () => stripeOnboardingStatus === "completed",
    [stripeOnboardingStatus]
  );

  const linkedBankCountryLabel = useMemo(() => {
    const countryCode = String(stripeConnectData?.stripeConnectMeta?.country || "").toUpperCase();
    if (!countryCode) return t("Unknown");
    return new Intl.DisplayNames([navigator.language || "en"], { type: "region" }).of(countryCode) || countryCode;
  }, [stripeConnectData?.stripeConnectMeta?.country, t]);

  const stripeDetailsSubmitted = useMemo(() => Boolean(stripeConnectData?.detailsSubmitted), [stripeConnectData]);

  const shouldShowSetupAlert = useMemo(
    () => !stripeOnboardingComplete && !stripeDetailsSubmitted,
    [stripeOnboardingComplete, stripeDetailsSubmitted]
  );

  const shouldShowEmbeddedNotificationBanner = useMemo(
    () => !stripeOnboardingComplete && stripeDetailsSubmitted,
    [stripeOnboardingComplete, stripeDetailsSubmitted]
  );

  const handleStartStripeOnboarding = async () => {
    try {
      onboardingRedirectingRef.current = false;
      setStartingOnboarding(true);
      const user = getAuth().currentUser;
      if (!user) {
        notify(t("Please sign in again to continue."), "error");
        return;
      }

      const idToken = await user.getIdToken();
      const isLocalhost = ["localhost", "127.0.0.1"].includes(window.location.hostname);
      const localFunctionsBase =
        process.env.REACT_APP_API_BASE_URL ||
        "https://us-central1-houari-boumedienne-schoo-4d4c2.cloudfunctions.net";
      const baseUrl = String(isLocalhost ? localFunctionsBase : process.env.REACT_APP_API_BASE_URL || "").replace(
        /\/+$/,
        ""
      );
      const endpoint = `${baseUrl}/api/stripe/connect/onboarding-link`;

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          returnUrl: "/teacher/services?stripeReturn=1",
          country: selectedOnboardingCountry || undefined,
        }),
      });

      const contentType = response.headers.get("content-type") || "";
      let payload = null;
      if (contentType.includes("application/json")) {
        payload = await response.json();
      } else {
        const raw = await response.text();
        throw new Error(
          raw.includes("<!DOCTYPE")
            ? t("API endpoint returned HTML. Check your API base URL configuration.")
            : t("Unexpected API response format.")
        );
      }

      if (!response.ok || !payload?.ok) {
        throw new Error(payload?.error || t("Unable to start Stripe onboarding."));
      }

      if (!payload?.url) {
        throw new Error(t("Stripe onboarding link is not available yet. Please contact support."));
      }

      onboardingRedirectingRef.current = true;
      window.setTimeout(() => {
        window.location.assign(payload.url);
      }, 120);
      return;
    } catch (error) {
      console.error(error);
      if (!onboardingRedirectingRef.current && pageAliveRef.current) {
        notify(error.message || t("Unable to start Stripe onboarding."), "error");
      }
    } finally {
      if (!onboardingRedirectingRef.current && pageAliveRef.current) {
        setStartingOnboarding(false);
      }
    }
  };

  useEffect(() => {
    pageAliveRef.current = true;
    return () => {
      pageAliveRef.current = false;
    };
  }, []);

  useEffect(() => {
    const refreshStripeStatus = async () => {
      const user = getAuth().currentUser;
      if (!user) return;

      // Refresh after return from Stripe, when we already have an account ID,
      // or when onboarding is still incomplete (re-check Stripe readiness).
      const searchParams = new URLSearchParams(window.location.search);
      const returnedFromStripe = searchParams.get("stripeReturn") === "1";
      const hasStripeAccount = Boolean(stripeConnectData?.stripeAccountId);
      const onboardingIncomplete = !stripeOnboardingComplete;
      if (!returnedFromStripe && !hasStripeAccount && !onboardingIncomplete) return;

      try {
        setStripeRefreshInProgress(true);
        const idToken = await user.getIdToken();
        const isLocalhost = ["localhost", "127.0.0.1"].includes(window.location.hostname);
        const localFunctionsBase =
          process.env.REACT_APP_API_BASE_URL ||
          "https://us-central1-houari-boumedienne-schoo-4d4c2.cloudfunctions.net";
        const baseUrl = String(isLocalhost ? localFunctionsBase : process.env.REACT_APP_API_BASE_URL || "").replace(
          /\/+$/,
          ""
        );
        const endpoint = `${baseUrl}/api/stripe/connect/refresh-status`;

        const response = await fetch(endpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
        });
        const payload = await response.json();
        if (!response.ok || !payload?.ok) {
          throw new Error(payload?.error || t("Unable to refresh Stripe status."));
        }

        if (pageAliveRef.current && payload?.stripeConnect) {
          setStripeConnectData(payload.stripeConnect);
        }
      } catch (error) {
        console.error(error);
        if (pageAliveRef.current) {
          notify(error.message || t("Unable to refresh Stripe status."), "error");
        }
      } finally {
        if (pageAliveRef.current) {
          setStripeRefreshInProgress(false);
        }
      }
    };

    refreshStripeStatus();
  }, [notify, stripeConnectData?.stripeAccountId, stripeOnboardingComplete, t]);

  useEffect(() => {
    const loadSupportedCountries = async () => {
      if (
        stripeOnboardingComplete ||
        stripeDetailsSubmitted ||
        startingOnboarding ||
        onboardingRedirectingRef.current
      ) {
        return;
      }
      const user = getAuth().currentUser;
      if (!user) return;

      try {
        setLoadingSupportedCountries(true);
        const idToken = await user.getIdToken();
        const isLocalhost = ["localhost", "127.0.0.1"].includes(window.location.hostname);
        const localFunctionsBase =
          process.env.REACT_APP_API_BASE_URL ||
          "https://us-central1-houari-boumedienne-schoo-4d4c2.cloudfunctions.net";
        const baseUrl = String(isLocalhost ? localFunctionsBase : process.env.REACT_APP_API_BASE_URL || "").replace(
          /\/+$/,
          ""
        );
        const endpoint = `${baseUrl}/api/stripe/connect/supported-countries`;

        const response = await fetch(endpoint, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        });
        const payload = await response.json();
        if (!response.ok || !payload?.ok || !Array.isArray(payload?.countries)) {
          throw new Error(payload?.error || t("Unable to load supported countries."));
        }

        if (onboardingRedirectingRef.current || !pageAliveRef.current) return;
        setSupportedCountries(payload.countries);
        setSelectedOnboardingCountry((prev) => {
          const nextAllowed = payload.countries.filter((countryCode) =>
            ONBOARDING_ALLOWED_COUNTRIES.includes(String(countryCode || "").toUpperCase())
          );
          if (prev && nextAllowed.includes(prev)) return prev;
          const existingCountry = String(stripeConnectData?.stripeConnectMeta?.country || "").toUpperCase();
          if (existingCountry && nextAllowed.includes(existingCountry)) return existingCountry;
          return nextAllowed[0] || "";
        });
      } catch (error) {
        console.error(error);
        if (!onboardingRedirectingRef.current && pageAliveRef.current) {
          notify(error.message || t("Unable to load supported countries."), "error");
        }
      } finally {
        if (!onboardingRedirectingRef.current && pageAliveRef.current) {
          setLoadingSupportedCountries(false);
        }
      }
    };

    loadSupportedCountries();
  }, [
    notify,
    startingOnboarding,
    stripeConnectData?.stripeConnectMeta?.country,
    stripeDetailsSubmitted,
    stripeOnboardingComplete,
    t,
  ]);

  useEffect(() => {
    const mountNotificationBanner = async () => {
      if (!shouldShowEmbeddedNotificationBanner) return;
      if (!notificationBannerContainerRef.current) return;

      if (!STRIPE_CONNECT_PUBLISHABLE_KEY) {
        setNotificationBannerError(t("Missing Stripe publishable key for embedded verification banner."));
        return;
      }

      const user = getAuth().currentUser;
      if (!user) return;

      try {
        setNotificationBannerError("");
        const idToken = await user.getIdToken();
        const isLocalhost = ["localhost", "127.0.0.1"].includes(window.location.hostname);
        const localFunctionsBase =
          process.env.REACT_APP_API_BASE_URL ||
          "https://us-central1-houari-boumedienne-schoo-4d4c2.cloudfunctions.net";
        const baseUrl = String(isLocalhost ? localFunctionsBase : process.env.REACT_APP_API_BASE_URL || "").replace(
          /\/+$/,
          ""
        );
        const endpoint = `${baseUrl}/api/stripe/connect/account-session`;

        const connectInstance = await loadConnectAndInitialize({
          publishableKey: STRIPE_CONNECT_PUBLISHABLE_KEY,
          fetchClientSecret: async () => {
            const response = await fetch(endpoint, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${idToken}`,
              },
            });
            const payload = await response.json();
            if (!response.ok || !payload?.ok || !payload?.clientSecret) {
              throw new Error(payload?.error || t("Unable to initialize Stripe verification banner."));
            }

            if (pageAliveRef.current && payload?.stripeConnect) {
              setStripeConnectData(payload.stripeConnect);
            }

            return payload.clientSecret;
          },
        });

        if (!pageAliveRef.current || !notificationBannerContainerRef.current) return;

        if (notificationBannerElementRef.current) {
          notificationBannerElementRef.current.remove();
          notificationBannerElementRef.current = null;
        }

        const banner = connectInstance.create("notification-banner");
        notificationBannerContainerRef.current.appendChild(banner);
        notificationBannerElementRef.current = banner;
      } catch (error) {
        console.error(error);
        if (pageAliveRef.current) {
          setNotificationBannerError(error.message || t("Unable to load Stripe verification tasks."));
        }
      }
    };

    mountNotificationBanner();

    return () => {
      if (notificationBannerElementRef.current) {
        notificationBannerElementRef.current.remove();
        notificationBannerElementRef.current = null;
      }
    };
  }, [shouldShowEmbeddedNotificationBanner, t]);

  const refreshAfterMutation = async () => {
    if (!teacherDocId) return;
    await fetchServices(teacherDocId);
  };

  const resetModalState = () => {
    setEditingServiceId("");
    setServiceForm(EMPTY_SERVICE_FORM);
    setModalPrices([]);
    setPriceForm(EMPTY_PRICE_FORM);
    setEditingPriceRegion("");
    setFormError("");
  };

  const openAddServiceModal = () => {
    resetModalState();
    setServiceModalOpen(true);
  };

  const openEditServiceModal = (service) => {
    setEditingServiceId(service.id);
    setServiceForm({
      name: service.name || "",
      description: service.description || "",
    });
    setModalPrices((service.prices || []).map((p) => ({
      regionKey: p.regionKey,
      currency: p.currency,
      amount: Number(p.amount || 0),
      billingType: p.billingType || "one_time",
      active: p.active !== false,
    })));
    setPriceForm(EMPTY_PRICE_FORM);
    setEditingPriceRegion("");
    setFormError("");
    setServiceModalOpen(true);
  };

  const closeServiceModal = () => {
    if (savingService) return;
    setServiceModalOpen(false);
  };

  const validateServiceForm = () => {
    if (!serviceForm.name.trim()) {
      setFormError(t("Service name is required"));
      return false;
    }

    if (modalPrices.length === 0) {
      setFormError(t("Please add at least one regional price"));
      return false;
    }

    const invalidPrice = modalPrices.find(
      (p) => !p.regionKey || !p.currency || !Number.isFinite(Number(p.amount)) || Number(p.amount) <= 0
    );
    if (invalidPrice) {
      setFormError(t("Each regional price must include valid amount and currency"));
      return false;
    }

    setFormError("");
    return true;
  };

  const handleSaveService = async () => {
    if (savingService) return;
    if (!validateServiceForm()) return;

    setSavingService(true);
    try {
      let targetTeacherDocId = teacherDocId;
      if (!targetTeacherDocId) {
        targetTeacherDocId = await resolveTeacherDocId();
        setTeacherDocId(targetTeacherDocId);
      }

      const uid = getAuth().currentUser?.uid || "";
      const payload = {
        name: serviceForm.name.trim(),
        description: serviceForm.description.trim(),
        status:
          services.find((service) => service.id === editingServiceId)?.status === "archived"
            ? "archived"
            : "published",
        updatedAt: serverTimestamp(),
        updatedBy: uid,
      };

      let targetServiceId = editingServiceId;

      if (editingServiceId) {
        await updateDoc(doc(db, "teachers", targetTeacherDocId, "services", editingServiceId), payload);
      } else {
        const newRef = await addDoc(collection(db, "teachers", targetTeacherDocId, "services"), {
          ...payload,
          status: "published",
          active: true,
          createdAt: serverTimestamp(),
        });
        targetServiceId = newRef.id;
      }

      const pricesColRef = collection(
        db,
        "teachers",
        targetTeacherDocId,
        "services",
        targetServiceId,
        "prices"
      );
      const existingSnap = await getDocs(pricesColRef);
      await Promise.all(existingSnap.docs.map((d) => deleteDoc(d.ref)));

      await Promise.all(
        modalPrices.map((price) =>
          setDoc(doc(pricesColRef, price.regionKey), {
            regionKey: price.regionKey,
            currency: price.currency,
            amount: Number(price.amount),
            billingType: price.billingType || "one_time",
            active: price.active !== false,
            updatedAt: serverTimestamp(),
          })
        )
      );

      await refreshAfterMutation();
      notify(editingServiceId ? t("Service updated successfully") : t("Service created successfully"));
      setServiceModalOpen(false);
    } catch (error) {
      console.error(error);
      notify(t("An error occurred while saving changes"), "error");
    } finally {
      setSavingService(false);
    }
  };

  const handleArchiveService = async (service) => {
    if (!teacherDocId) return;

    try {
      await updateDoc(doc(db, "teachers", teacherDocId, "services", service.id), {
        status: "archived",
        active: false,
        updatedAt: serverTimestamp(),
        updatedBy: getAuth().currentUser?.uid || "",
      });
      await refreshAfterMutation();
      notify(t("Service status updated"));
    } catch (error) {
      console.error(error);
      notify(t("An error occurred while saving changes"), "error");
    }
  };

  const handleRepublishService = async (service) => {
    if (!teacherDocId) return;

    try {
      await updateDoc(doc(db, "teachers", teacherDocId, "services", service.id), {
        status: "published",
        active: true,
        updatedAt: serverTimestamp(),
        updatedBy: getAuth().currentUser?.uid || "",
      });
      await refreshAfterMutation();
      notify(t("Service status updated"));
    } catch (error) {
      console.error(error);
      notify(t("An error occurred while saving changes"), "error");
    }
  };

  const validatePriceForm = () => {
    if (!priceForm.regionKey) return t("Region is required");
    if (!priceForm.currency) return t("Currency is required");
    const amount = Number(priceForm.amount);
    if (!Number.isFinite(amount) || amount <= 0) return t("Amount must be greater than 0");
    return "";
  };

  const handleSaveModalPrice = () => {
    const validationError = validatePriceForm();
    if (validationError) {
      notify(validationError, "error");
      return;
    }

    const regionExists = modalPrices.some((p) => p.regionKey === priceForm.regionKey);
    if (regionExists && editingPriceRegion !== priceForm.regionKey) {
      notify(t("This region already has a price. Edit it instead of adding a duplicate."), "error");
      return;
    }

    const nextPrice = {
      regionKey: priceForm.regionKey,
      currency: priceForm.currency,
      amount: Number(priceForm.amount),
      billingType: priceForm.billingType,
      active: priceForm.active,
    };

    setModalPrices((prev) => {
      if (!editingPriceRegion) {
        return [...prev, nextPrice];
      }
      return prev
        .filter((p) => p.regionKey !== editingPriceRegion)
        .concat(nextPrice)
        .sort((a, b) => a.regionKey.localeCompare(b.regionKey));
    });

    setPriceForm(EMPTY_PRICE_FORM);
    setEditingPriceRegion("");
  };

  const handleEditModalPrice = (price) => {
    setPriceForm({
      regionKey: price.regionKey,
      currency: price.currency,
      amount: String(price.amount ?? ""),
      billingType: price.billingType || "one_time",
      active: price.active !== false,
    });
    setEditingPriceRegion(price.regionKey);
  };

  const handleRemoveModalPrice = (regionKey) => {
    setModalPrices((prev) => prev.filter((p) => p.regionKey !== regionKey));
    if (editingPriceRegion === regionKey) {
      setEditingPriceRegion("");
      setPriceForm(EMPTY_PRICE_FORM);
    }
  };

  const handleToggleModalPriceActive = (regionKey) => {
    setModalPrices((prev) =>
      prev.map((p) => (p.regionKey === regionKey ? { ...p, active: !p.active } : p))
    );
  };

  return (
    <Box
      sx={{
        background:
          "radial-gradient(1200px 600px at 10% -10%, rgba(255, 204, 155, 0.35), transparent 60%), radial-gradient(900px 500px at 100% 0%, rgba(130, 189, 255, 0.35), transparent 60%), #f6f7fb",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <TeacherNavbar />

      <Box sx={{ p: { xs: 1, md: 2 } }}>
        <Card
          elevation={0}
          sx={{
            mb: 2.5,
            borderRadius: 4,
            px: 3,
            py: 2.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: "linear-gradient(180deg, #ffffff 0%, #f9fafb 100%)",
            boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
            border: "1px solid rgba(0,0,0,0.06)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Avatar
                sx={{
                  bgcolor: "#eef2ff",
                  color: "#4f46e5",
                  width: 46,
                  height: 46,
                  boxShadow: "0 8px 18px rgba(79,70,229,0.18)",
                }}
              >
                <SellOutlinedIcon />
              </Avatar>
              <Box sx={{ textAlign: "left" }}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  {t("Services")}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {t("Manage your services and regional pricing")}
                </Typography>
              </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="contained"
                size="small"
                onClick={openAddServiceModal}
                startIcon={<AddIcon />}
              >
                {t("Add service")}
              </Button>
          </Box>
        </Card>

        {shouldShowSetupAlert && (
          <Paper
            elevation={0}
            sx={{
              mb: 2.5,
              borderRadius: 1.5,
              border: "1px solid #e9c28b",
              backgroundColor: "#f7f1e6",
              p: 1.5,
            }}
          >
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ minWidth: 0 }}>
                <WarningAmberRoundedIcon
                  sx={{
                    color: "#a16207",
                    mt: 0.15,
                  }}
                />
                <Box>
                  <Typography sx={{ fontWeight: 700, color: "#2f2f2f", mb: 0.5 }}>
                    {t("You need to complete your payout setup before accepting student payments.")}
                  </Typography>
                  <Typography sx={{ color: "#4b5563" }}>
                    {t("Submit your identity and bank details on Stripe to activate payouts for your services.")}
                  </Typography>
                </Box>
              </Stack>

              <Box sx={{ flexShrink: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <FormControl size="small" sx={{ minWidth: 160, bgcolor: "#fffaf2" }}>
                    <InputLabel>{t("Country")}</InputLabel>
                    <Select
                      native
                      label={t("Country")}
                      value={selectedOnboardingCountry}
                      onChange={(e) => setSelectedOnboardingCountry(e.target.value)}
                      disabled={loadingSupportedCountries || filteredOnboardingCountries.length === 0}
                    >
                      <option value="" disabled>
                        {t("Country")}
                      </option>
                      {filteredOnboardingCountries.map((countryCode) => (
                        <option key={countryCode} value={countryCode}>
                          {new Intl.DisplayNames([navigator.language || "en"], { type: "region" }).of(countryCode) ||
                            countryCode}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  <Button
                    variant="outlined"
                    onClick={handleStartStripeOnboarding}
                    disabled={startingOnboarding || loadingSupportedCountries || !selectedOnboardingCountry}
                    sx={{
                      borderColor: "#d7b482",
                      color: "#3f3f46",
                      bgcolor: "#fffaf2",
                      textTransform: "none",
                      "&:hover": {
                        borderColor: "#c99b5f",
                        bgcolor: "#fff4e3",
                      },
                    }}
                  >
                    Complete payout setup
                  </Button>
                </Stack>
              </Box>
            </Stack>
          </Paper>
        )}

        {shouldShowEmbeddedNotificationBanner && (
          <Paper
            elevation={0}
            sx={{
              mb: 2.5,
              borderRadius: 1.5,
              border: "1px solid rgba(59,130,246,0.35)",
              backgroundColor: "#eff6ff",
              p: 1.5,
            }}
          >
            <Stack spacing={1.5}>
              <Typography sx={{ fontWeight: 700, color: "#1e3a8a" }}>
                {t("Action required on your Stripe account")}
              </Typography>

              <Box ref={notificationBannerContainerRef} />

              {notificationBannerError && (
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                  <Typography sx={{ color: "#1f2937" }}>
                    {notificationBannerError}
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={handleStartStripeOnboarding}
                    disabled={startingOnboarding}
                    sx={{
                      borderColor: "rgba(59,130,246,0.45)",
                      color: "#1e40af",
                      bgcolor: "#dbeafe",
                      textTransform: "none",
                      "&:hover": {
                        borderColor: "#3b82f6",
                        bgcolor: "#bfdbfe",
                      },
                    }}
                  >
                    {t("Open Stripe verification")}
                  </Button>
                </Stack>
              )}
            </Stack>
          </Paper>
        )}

        {(startingOnboarding || stripeRefreshInProgress) && (
          <Paper
            elevation={0}
            sx={{
              mb: 2,
              borderRadius: 1.5,
              border: "1px solid rgba(59,130,246,0.28)",
              backgroundColor: "rgba(239,246,255,0.9)",
              p: 1.5,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={18} thickness={5} />
              <Typography sx={{ fontWeight: 600, color: "#1f2937" }}>
                {startingOnboarding
                  ? t("Preparing your Stripe onboarding...")
                  : t("Refreshing Stripe onboarding status...")}
              </Typography>
            </Stack>
          </Paper>
        )}

        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            border: "1px solid rgba(0,0,0,0.06)",
            overflow: "hidden",
            bgcolor: "#fff",
            boxShadow: "0 12px 30px rgba(0,0,0,0.06)",
          }}
        >
          <Box sx={{ px: 2, pt: 1.5, borderBottom: "1px solid #E5E7EB" }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={{ xs: 1.2, md: 2 }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "center" }}
            >
              <Stack direction="row" spacing={3}>
                {STATUS_FILTERS.map((status) => {
                  const active = statusFilter === status;
                  return (
                    <Button
                      key={status}
                      onClick={() => setStatusFilter(status)}
                      disableRipple
                      sx={{
                        minWidth: "unset",
                        px: 0,
                        pb: 1.1,
                        borderRadius: 0,
                        borderBottom: active ? "3px solid #4f46e5" : "3px solid transparent",
                        color: active ? "#111827" : "#6b7280",
                        fontWeight: active ? 700 : 500,
                        textTransform: "none",
                      }}
                    >
                      {status === "all" ? t("All") : t(status.charAt(0).toUpperCase() + status.slice(1))}
                    </Button>
                  );
                })}
              </Stack>

              <Box
                sx={{
                  px: 1.5,
                  py: 0.75,
                  borderRadius: "7px",
                  border: hasLinkedBankAccount ? "1px solid #86efac" : "1px solid #fca5a5",
                  bgcolor: hasLinkedBankAccount ? "#f0fdf4" : "#fef2f2",
                  color: hasLinkedBankAccount ? "#15803d" : "#b91c1c",
                  fontWeight: 700,
                  fontSize: 13,
                  whiteSpace: "nowrap",
                }}
              >
                {hasLinkedBankAccount
                  ? `${t("Linked Bank Account in")}: ${linkedBankCountryLabel}`
                  : t("No Bank Account Found")}
              </Box>
            </Stack>
          </Box>

          <Table
            sx={{
              minWidth: 1100,
              "& .MuiTableCell-root": {
                borderColor: "#E7EAF2",
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, py: 2.25, bgcolor: "#F8FAFC", color: "#0f172a" }}>{t("Service")}</TableCell>
                <TableCell sx={{ fontWeight: 700, py: 2.25, bgcolor: "#F8FAFC", color: "#0f172a" }}>{t("Status")}</TableCell>
                <TableCell sx={{ fontWeight: 700, py: 2.25, bgcolor: "#F8FAFC", color: "#0f172a" }}>{t("Country")}</TableCell>
                <TableCell sx={{ fontWeight: 700, py: 2.25, bgcolor: "#F8FAFC", color: "#0f172a" }}>{t("Price")}</TableCell>
                <TableCell sx={{ fontWeight: 700, py: 2.25, bgcolor: "#F8FAFC", color: "#0f172a" }}>{t("Actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">{t("Loading...")}</TableCell>
                </TableRow>
              ) : filteredServices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">{t("No services found")}</TableCell>
                </TableRow>
              ) : (
                filteredServices.map((service) => {
                  const activePrices = (service.prices || []).filter((p) => p.active !== false);
                  const activePricesByRegionOrder = REGION_OPTIONS
                    .map((region) => activePrices.find((price) => price.regionKey === region.key))
                    .filter(Boolean);
                  const remainingActivePrices = activePrices.filter(
                    (price) => !REGION_OPTIONS.some((region) => region.key === price.regionKey)
                  );
                  const orderedActivePrices = activePricesByRegionOrder.concat(remainingActivePrices);
                  return (
                    <TableRow
                      key={service.id}
                      sx={{
                        cursor: "default",
                        transition: "background-color 180ms ease",
                        "& td": { borderBottom: "1px solid #ECEFF3", py: 2.5, verticalAlign: "middle" },
                        "&:hover": {
                          backgroundColor: "#fcfcfcff",
                        },
                      }}
                    >
                      <TableCell>
                        <Stack direction="row" spacing={1.2} alignItems="center">
                          <Box
                            sx={{
                              width: 42,
                              height: 42,
                              borderRadius: 1,
                              bgcolor: "#EEF2FF",
                              color: "#4f46e5",
                              display: "grid",
                              placeItems: "center",
                              fontWeight: 800,
                              fontSize: 13,
                            }}
                          >
                            {(service.name || "S").slice(0, 2).toUpperCase()}
                          </Box>
                          <Box>
                            <Typography sx={{ fontWeight: 700, color: "#0f172a", letterSpacing: "-0.01em" }}>
                              {service.name || t("Untitled")}
                            </Typography>
                            <Typography variant="body2" sx={{ color: "#667085", mt: 0.25 }}>
                              {service.description || t("No description")}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Chip size="small" label={t(service.status)} sx={statusChipSx(service.status)} />
                      </TableCell>

                      <TableCell>
                        {orderedActivePrices.length > 0 ? (
                          <Stack spacing={0.8}>
                            {orderedActivePrices.map((price) => (
                              <Chip
                                key={price.regionKey}
                                size="small"
                                label={t(mapRegionLabel(price.regionKey))}
                                sx={{
                                  width: "fit-content",
                                  borderRadius: "999px",
                                  height: 26,
                                  bgcolor: "#EEF2FF",
                                  color: "#3730A3",
                                  fontWeight: 600,
                                }}
                              />
                            ))}
                          </Stack>
                        ) : (
                          <Typography sx={{ color: "#6b7280" }}>{t("No active prices")}</Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        {orderedActivePrices.length > 0 ? (
                          <Stack spacing={0.8}>
                            {orderedActivePrices.map((price) => (
                              <Typography
                                key={`${price.regionKey}-price`}
                                sx={{ color: "#111827", fontWeight: 700, letterSpacing: "-0.01em" }}
                              >
                                {Number(price.amount).toFixed(2)} {price.currency}
                              </Typography>
                            ))}
                          </Stack>
                        ) : (
                          <Typography sx={{ color: "#6b7280" }}>-</Typography>
                        )}
                      </TableCell>

                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Stack direction="row" spacing={0.8}>
                          <IconButton
                            size="small"
                            onClick={() => openEditServiceModal(service)}
                            title={t("Edit service")}
                            sx={{
                              border: "1px solid #E5E7EB",
                              bgcolor: "#FFFFFF",
                              "&:hover": { bgcolor: "#F8FAFC", borderColor: "#CBD5E1" },
                            }}
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                          {service.status !== "archived" ? (
                            <IconButton
                              size="small"
                              onClick={() => handleArchiveService(service)}
                              title={t("Archive")}
                              sx={{
                                border: "1px solid #FECACA",
                                bgcolor: "#FFF1F2",
                                "&:hover": { bgcolor: "#FFE4E6", borderColor: "#FCA5A5" },
                              }}
                            >
                              <ArchiveOutlinedIcon fontSize="small" sx={{ color: "#ef4444" }} />
                            </IconButton>
                          ) : (
                            <IconButton
                              size="small"
                              onClick={() => handleRepublishService(service)}
                              title={t("Republish")}
                              sx={{
                                border: "1px solid #BBF7D0",
                                bgcolor: "#F0FDF4",
                                "&:hover": { bgcolor: "#DCFCE7", borderColor: "#86EFAC" },
                              }}
                            >
                              <UnarchiveOutlinedIcon fontSize="small" sx={{ color: "#16a34a" }} />
                            </IconButton>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Paper>

      </Box>

      <Dialog
        open={serviceModalOpen}
        onClose={closeServiceModal}
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: "0 20px 60px rgba(15, 23, 42, 0.18)",
          },
        }}
      >
        <DialogContent
          sx={{
            p: { xs: 2, md: 3 },
            bgcolor: "background.default",
            "& .MuiOutlinedInput-root": { borderRadius: 2 },
            "& .MuiInputBase-root": { borderRadius: 2 },
            "& .MuiButton-containedPrimary": {
              borderRadius: 2,
              bgcolor: "primary.main",
              "&:hover": { bgcolor: "primary.dark" },
            },
            "& .MuiButton-outlinedPrimary": { borderRadius: 2 },
          }}
        >
          <Stack spacing={2}>
            <Box sx={{ textAlign: "center", mb: 1 }}>
              <Typography sx={{ fontSize: 22, fontWeight: 800 }}>
                {editingServiceId ? t("Edit service") : t("Add service")}
              </Typography>
            </Box>

            <Box>
              <Stack spacing={1.5}>
                <TextField
                  label={t("Service name")}
                  value={serviceForm.name}
                  onChange={(e) => setServiceForm((prev) => ({ ...prev, name: e.target.value }))}
                  fullWidth
                />
                <TextField
                  label={t("Description")}
                  value={serviceForm.description}
                  onChange={(e) => setServiceForm((prev) => ({ ...prev, description: e.target.value }))}
                  multiline
                  minRows={3}
                  fullWidth
                />
              </Stack>
            </Box>

            <Divider />

            <Box>
              <Typography sx={{ fontWeight: 700, mb: 1 }}>{t("Regional pricing")}</Typography>
              <Stack spacing={1.5}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                  <FormControl fullWidth>
                    <InputLabel>{t("Region")}</InputLabel>
                    <Select
                      label={t("Region")}
                      value={priceForm.regionKey}
                      onChange={(e) => setPriceForm((prev) => ({ ...prev, regionKey: e.target.value }))}
                    >
                      {REGION_OPTIONS.map((region) => (
                        <MenuItem key={region.key} value={region.key}>
                          {t(region.label)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth>
                    <InputLabel>{t("Currency")}</InputLabel>
                    <Select
                      label={t("Currency")}
                      value={priceForm.currency}
                      onChange={(e) => setPriceForm((prev) => ({ ...prev, currency: e.target.value }))}
                    >
                      {CURRENCY_OPTIONS.map((currency) => (
                        <MenuItem key={currency} value={currency}>
                          {currency}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                  <TextField
                    label={t("Amount")}
                    type="number"
                    value={priceForm.amount}
                    onChange={(e) => setPriceForm((prev) => ({ ...prev, amount: e.target.value }))}
                    fullWidth
                  />

                  <FormControl fullWidth>
                    <InputLabel>{t("Billing type")}</InputLabel>
                    <Select
                      label={t("Billing type")}
                      value={priceForm.billingType}
                      onChange={(e) => setPriceForm((prev) => ({ ...prev, billingType: e.target.value }))}
                    >
                      {BILLING_OPTIONS.map((billing) => (
                        <MenuItem key={billing.key} value={billing.key}>
                          {t(billing.label)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>

                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography>{t("Active")}</Typography>
                  <Switch
                    checked={priceForm.active}
                    onChange={(e) => setPriceForm((prev) => ({ ...prev, active: e.target.checked }))}
                  />
                </Stack>

                <Stack direction="row" spacing={1}>
                  <Button variant="contained" onClick={handleSaveModalPrice}>
                    {editingPriceRegion ? t("Update regional price") : t("Add regional price")}
                  </Button>
                  {editingPriceRegion && (
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setEditingPriceRegion("");
                        setPriceForm(EMPTY_PRICE_FORM);
                      }}
                    >
                      {t("Cancel")}
                    </Button>
                  )}
                </Stack>

                <Stack spacing={1}>
                  {modalPrices.length === 0 ? (
                    <Typography color="text.secondary">{t("No regional prices yet")}</Typography>
                  ) : (
                    modalPrices.map((price) => (
                      <Paper key={price.regionKey} sx={{ p: 1.25, borderRadius: 1.2, border: "1px solid #E5E7EB" }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                          <Box>
                            <Typography sx={{ fontWeight: 700 }}>
                              {t(mapRegionLabel(price.regionKey))} - {Number(price.amount).toFixed(2)} {price.currency}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "#6b7280" }}>
                              {t("Billing")}: {t(BILLING_OPTIONS.find((b) => b.key === price.billingType)?.label || "One time")}
                            </Typography>
                          </Box>

                          <Stack direction="row" spacing={0.5}>
                            <Chip
                              label={price.active ? t("Active") : t("Inactive")}
                              size="small"
                              sx={price.active ? statusChipSx("published") : statusChipSx("archived")}
                            />
                            <IconButton size="small" onClick={() => handleEditModalPrice(price)}>
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleToggleModalPriceActive(price.regionKey)}>
                              <Switch checked={price.active} size="small" />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => handleRemoveModalPrice(price.regionKey)}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        </Stack>
                      </Paper>
                    ))
                  )}
                </Stack>
              </Stack>
            </Box>

            {formError && <Alert severity="error">{formError}</Alert>}

            <Box
              sx={{
                bgcolor: "background.paper",
                borderTop: "1px solid rgba(15, 23, 42, 0.08)",
                boxShadow: "0 -8px 18px rgba(15, 23, 42, 0.06)",
                mt: 1,
              }}
            >
              <Button
                onClick={handleSaveService}
                variant="contained"
                fullWidth
                disabled={savingService}
                sx={{ fontWeight: 700 }}
              >
                {savingService ? t("Saving...") : t("Save service")}
              </Button>
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default TeacherServicesPage;
